import { useId, useRef, useState } from "react";
import { QRCodeSVG } from "qrcode.react";
import { identitySecurity, loadIdentity } from "./identityClient";
import { useIdentity } from "./useIdentity";
import type { Proof } from "./generated/native-identity";
import "./identityControls.css";

/** Embedded in existing account sections; never stores passwords, setup keys or recovery codes. */
export function IdentitySecurityControls() {
  const account = useIdentity();
  const context = account.session
    ? `${account.session.tenantId}:${account.session.user.id}:${account.session.workspace.id}`
    : "signed-out";
  return <SecurityFields key={context} />;
}

function SecurityFields() {
  const account = useIdentity();
  const profile = account.security;
  const id = useId();
  const [password, setPassword] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [confirmation, setConfirmation] = useState("");
  const [factor, setFactor] = useState("");
  const [setup, setSetup] = useState<{ secret: string; otpauthUri: string } | null>(null);
  const [codes, setCodes] = useState<string[]>([]);
  const [message, setMessage] = useState("");
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);
  const locked = useRef(false);
  const run = async (action: () => Promise<void>) => {
    if (locked.current) return;
    locked.current = true;
    setBusy(true);
    setError("");
    setMessage("");
    try {
      await action();
    } catch (e) {
      setError(e instanceof Error ? e.message : "Unable to update account security.");
    } finally {
      locked.current = false;
      setBusy(false);
    }
  };
  const clearProof = () => {
    setPassword("");
    setNewPassword("");
    setConfirmation("");
    setFactor("");
  };
  const proof = (): Proof => ({
    currentPassword: password,
    ...(factor.trim()
      ? /^\d{6}$/.test(factor.trim())
        ? { code: factor.trim() }
        : { recoveryCode: factor.trim() }
      : {}),
  });
  const manage = (operation: Parameters<typeof identitySecurity.manage>[0]) =>
    void run(async () => {
      const result = await identitySecurity.manage(operation, {
        ...proof(),
        ...(operation === "password/change" ? { newPassword } : {}),
      });
      // Preserve one-time codes before the cookie refresh, which may fail independently.
      if (result.recoveryCodes) setCodes(result.recoveryCodes);
      clearProof();
      setSetup(null);
      if (result.requiresSignIn) {
        window.location.reload();
        return;
      }
      setMessage("Account security updated.");
      await identitySecurity.refresh();
    });
  return (
    <section className="identity-controls" aria-label="Personal account security">
      <h3>Personal account security</h3>
      <p>Manage your Academy password, authenticator and signed-in devices.</p>
      {(error || account.error) && <p role="alert">{error || account.error}</p>}
      {message && <p role="status">{message}</p>}
      {!profile && (
        <button type="button" disabled={busy} onClick={() => void run(loadIdentity)}>
          Refresh account security
        </button>
      )}
      {profile && (
        <fieldset disabled={busy}>
          <p>
            {profile.email} · Authenticator {profile.mfaEnabled ? "enabled" : "not enabled"} ·{" "}
            {profile.recoveryCodesRemaining} recovery codes remaining
          </p>
          <div className="identity-fields">
            <label htmlFor={`${id}-password`}>
              Current password
              <input
                id={`${id}-password`}
                type="password"
                autoComplete="current-password"
                value={password}
                onChange={e => setPassword(e.target.value)}
              />
            </label>
            <label htmlFor={`${id}-factor`}>
              Authenticator or recovery code
              <input
                id={`${id}-factor`}
                autoComplete="one-time-code"
                value={factor}
                onChange={e => setFactor(e.target.value)}
              />
            </label>
          </div>
          {!profile.mfaEnabled && !setup && (
            <button
              type="button"
              disabled={!password}
              onClick={() =>
                void run(async () => {
                  setSetup(await identitySecurity.enrol(password));
                  clearProof();
                  setCodes([]);
                })
              }
            >
              Set up authenticator
            </button>
          )}
          {setup && (
            <div className="identity-setup">
              <p>Scan with your authenticator, then enter its six-digit code above.</p>
              <QRCodeSVG
                value={setup.otpauthUri}
                size={200}
                marginSize={4}
                level="M"
                role="img"
                aria-label="Authenticator setup QR code"
              />
              <label>
                Manual setup key
                <input readOnly value={setup.secret} />
              </label>
              <div className="identity-actions">
                <button
                  type="button"
                  disabled={!/^\d{6}$/.test(factor.trim())}
                  onClick={() =>
                    void run(async () => {
                      const result = await identitySecurity.confirm(factor.trim());
                      setCodes(result.recoveryCodes);
                      setSetup(null);
                      clearProof();
                      setMessage("Authenticator enabled. Save your recovery codes.");
                      await identitySecurity.refresh();
                    })
                  }
                >
                  Confirm authenticator
                </button>
                <button
                  type="button"
                  onClick={() => {
                    setSetup(null);
                    clearProof();
                  }}
                >
                  Cancel setup
                </button>
              </div>
            </div>
          )}
          {profile.mfaEnabled && (
            <div className="identity-actions">
              <button
                type="button"
                disabled={!password || !factor.trim()}
                onClick={() => manage("mfa/verify")}
              >
                Verify sensitive access
              </button>
              <button
                type="button"
                disabled={!password || !factor.trim()}
                onClick={() => manage("mfa/recovery-codes")}
              >
                Replace recovery codes
              </button>
              <button
                type="button"
                disabled={!password || !factor.trim()}
                onClick={() => manage("mfa/disable")}
              >
                Disable authenticator &amp; sign out
              </button>
            </div>
          )}
          <details>
            <summary>Change password</summary>
            <div className="identity-fields">
              <label>
                New password (12–128 characters)
                <input
                  type="password"
                  autoComplete="new-password"
                  value={newPassword}
                  onChange={e => setNewPassword(e.target.value)}
                  minLength={12}
                  maxLength={128}
                />
              </label>
              <label>
                Confirm new password
                <input
                  type="password"
                  autoComplete="new-password"
                  value={confirmation}
                  onChange={e => setConfirmation(e.target.value)}
                />
              </label>
            </div>
            <p>Changing your password signs out all devices.</p>
            <button
              type="button"
              disabled={
                !password ||
                newPassword.length < 12 ||
                newPassword.length > 128 ||
                newPassword !== confirmation ||
                (profile.mfaEnabled && !factor.trim())
              }
              onClick={() => manage("password/change")}
            >
              Change password &amp; sign out
            </button>
          </details>
          <details data-identity-section="sessions">
            <summary>Active sessions ({profile.sessions.length})</summary>
            {profile.sessions.map(session => (
              <div className="identity-session" key={session.id}>
                <div>
                  <b>{session.id === profile.currentSessionId ? "This device" : "Signed-in device"}</b>
                  <p>{session.user_agent || "Device not reported"}</p>
                  <small>
                    {session.ip_address} · Last active {new Date(session.last_seen_at).toLocaleString()}
                  </small>
                </div>
                <button
                  type="button"
                  onClick={() =>
                    void run(async () => {
                      const result = await identitySecurity.revoke(session.id);
                      if (result.requiresSignIn) window.location.reload();
                      else setMessage("Session signed out.");
                    })
                  }
                >
                  {session.id === profile.currentSessionId ? "Sign out this device" : "Revoke session"}
                </button>
              </div>
            ))}
          </details>
        </fieldset>
      )}
      {codes.length > 0 && (
        <div className="identity-recovery">
          <h4>Save your recovery codes</h4>
          <p>Shown once. Keep them privately offline. Each code can be used once.</p>
          {codes.map(code => (
            <p key={code}>
              <code>{code}</code>
            </p>
          ))}
          <button type="button" onClick={() => setCodes([])}>
            I have saved my recovery codes
          </button>
        </div>
      )}
    </section>
  );
}
